import { COMPARISONS } from '../constants/config/filters';
import * as HASH from '../constants/config/hash';
import { convertMagnitude } from '../utilities/magnitudes';

/**
 * KEEP THIS AT THE TOP!
 * @name parameterId
 * @function 
 * @description
 * @param {string} [id='']
 */
export const parameterId = ( id='' ) => HASH.PARAMETER_VALUE.replace( '{id}', id );

/**
 * KEEP THIS AT THE TOP!
 * @name parseHash
 * @function
 * @description Breaks hash into an object
 * @param {string} hash
 * @returns {Object} Object formatted with `id` and `values` keys
 */
export const parseHash = hash => {
    let ary = hash.split( '=' ),
    id = ary[ 0 ].replace( parameterId(), '' ),
    values = ary[ 1 ].split( '|' );

    if( ary[ 1 ].length === 0 ) return { id, values: [ COMPARISONS.NONE ] };

    return { id, values };
};

/**
 * @name checkboxToHash
 * @function 
 * @description Formats a group of checkboxes and applies it to the hash 
 * @param {string} id ID of the parameter to format
 * @param {array} [values=[]] Values for the text filter
 * @param {boolean} [includeBlanks=false] Determines if the filter value should exclude parts with no value
 */
export const checkboxToHash = ( id, values=[], includeBlanks=false ) => `${ parameterId( id ) }=${ includeBlanks ? '|' : '' }${ values.map(v => encodeURIComponent(v)).join( '|' ) }`;

/**
 * @name compareToHash
 * @function
 * @description 
 * @param {array} [parts=[]] Array of parts to compare
 */
export const compareToHash = ( parts=[] ) => `${ HASH.COMPARE }=${ parts.join( '|' ) }`;

/**
 * @name findHashValue
 * @function
 * @description Returns a value from the hash
 * @param {string} key 
 * @returns {string} Hash value
 */
export const findHashValue = key => {
    let hash = window.location.hash,
    hashAry = hash.includes( HASH.INITIATOR ) ? hash.substr( HASH.INITIATOR.length ).split( '&' ) : [],
    regexp = new RegExp( `^${ key }=(.*?)$`, 'g' );
    return hashAry.find( item => regexp.test( item ) );
}

/**
 * @name updateHash
 * @function
 * @description Retrieves a parameter from the hash
 * @param {string} id ID of the parameter to retrieve
 * @param {null|string} hash Hash to add. If `null` removes the hash entirely
 * @param {boolean} [isFilter=true] Determines if the value is a parameter filter.
 */
export const updateHash = ( id, hash ) => {

    let oldHash = window.location.hash.replace( HASH.INITIATOR, '' ),
    ary = oldHash.length > 0 ? oldHash.split( '&' ) : [],
    index = ary.findIndex( item => item.includes( id + '=' ) ),
    currentUrl = `${ window.location.protocol }//${ window.location.host }${ window.location.pathname }`;

    // if param exists
    if( index > -1 ){

        // if the hash exists
        if( hash ){
            ary[ index ] = hash;
        
        // remove the hash entirely
        } else {
          ary.splice( index, 1 );
        }

    // if hash exists but this filter doesn't
    } else {
        if( hash ) ary.push( hash );
    }

    window.location = currentUrl + window.location.search + HASH.INITIATOR + ary.join( '&' );

};

/**
 * @name hashToCheckbox
 * @function
 * @description Converts checkbox hash to filter object
 */
export const hashToCheckbox = hash => {
    let data = parseHash( hash );
    return { 
        [ data.id ]: data.values.map(
            value => {

                // already docoded value is passed.
                // value = decodeURIComponent( value );

                let comparison = COMPARISONS.EXACT;

                if( value === COMPARISONS.ALL ) comparison = COMPARISONS.ALL;
                if( value === COMPARISONS.NONE ) comparison = COMPARISONS.NONE;

                return ({
                    comparison,
                    value
                })
            }
        )
    };
};

/**
 * @name hashToCompare
 * @function
 * @description Converts comparison hash to filter object
 */
export const hashToCompare = hash => parseHash( hash ).values;

/**
 * @name hashToParameters
 * @function
 * @description Converts displayed parameters hash to list of parameters
 */
export const hashToParameters = hash => {
    let data = parseHash( hash );
    return data.values;
};

/**
 * @name hashToRange
 * @function
 * @description Converts range hash to filter object
 */
export const hashToRange = hash => {
    let data = parseHash( hash ),
    minRange = decodeURIComponent( data.values[ 0 ] ),
    maxRange = decodeURIComponent( data.values[ 1 ] );

    if( isNaN( minRange ) && minRange !== COMPARISONS.MIN ) minRange = convertMagnitude( minRange );
    if( isNaN( maxRange ) && maxRange !== COMPARISONS.MAX ) maxRange = convertMagnitude( maxRange );

    return {
        [ data.id ]: [
            {
                comparison: COMPARISONS.GREATER_THAN,
                value: minRange
            },
            {
                comparison: COMPARISONS.LESS_THAN,
                value: maxRange
            }
        ]
    }
};

/**
 * @name hashToText
 * @function
 * @description Converts text hash to filter object
 */
export const hashToText = hash => {
    let data = parseHash( hash );
    return { 
        [ data.id ]: data.values.map(
            value => ({
                comparison: COMPARISONS.INCLUDES,
                value: decodeURIComponent( value )
            })
        )
    };
};

/**
 * @name hashToQsfv
 * @function
 * @description Converts Quick Search Filter Value (QSFV) to filter object
 * @param {*} hash 
 */
export const hashToQsfv = hash => {
    let ary = hash.split( '=' ),
    fields = ary[ 1 ].split( '_' ),
    data = fields.map( field => {
        let ary = field.split( '|' ),
        key = ary[ 0 ],
        value = ary[ 1 ]
        return { key, value };
    });
    
    return data
}

/**
 * @name parametersToHash
 * @function
 * @description Converts list of parameters to the hash
 * @param {array} parameters 
 * @returns {string} displayed parameters hash
 */
export const parametersToHash = parameters => {
    return `${ HASH.PARAMETERS_DISPLAY }=${ parameters.join( '|' ) }`;
}

/**
 * @name rangeToHash
 * @function
 * @description Formats a range value and applies it to the hash 
 * @param {string} id ID of the parameter to format
 * @param {number} [max=""] Maximum range value 
 * @param {number} [min=""] Minimum range value
 * @returns {string} Properly formatted hash value (e.g. `pId=min|max` )
 */
export const rangeToHash = ( id, max='', min='' ) => `${ parameterId( id ) }=${ min }|${ max }`;

/**
 * @name sortToHash
 * @function
 * @description Converts list of parameters to the hash
 * @param {array} parameters 
 * @returns {string} displayed parameters hash
 */
export const sortToHash = parameters => {
    return `${ HASH.SORT }=${ parameters.join( '|' ) }`;
}

/**
 * @name hashToSort
 * @function
 * @description Converts list of parameters to the hash
 * @param {string} hash 
 * @returns {array} displayed parameters hash
 */
export const hashToSort = hash => {
    let ary = hash.split( '=' ),
    values = ary[ 1 ].split( '|' );
    if( ary[ 1 ].length === 0 ) return [];
    return values.map( item => item.split( ',' ) );
}


/**
 * @name textToHash
 * @function 
 * @description Formats a text value and applies it to the hash 
 * @param {string} id ID of the parameter to format
 * @param {string} values Values for the text filter
 */
export const textToHash = ( id, values ) => `${ parameterId( id ) }=${ values }`;

/**
 * @name hashToValues
 * @function
 * @description Converts hash to the list of values
 * @param {string} hash 
 * @returns {array} displayed array of hash values
 */
 export const hashToValues = hash => {
    let ary = hash.split( '=' ),
    values = ary[ 1 ].split( '|' );
    if( ary[ 1 ].length === 0 ) return [];
    return values;
}

/**
 * @name hashToSelectedValues
 * @function
 * @description Converts hash of selected rows to array
 * @returns {array} displayed array of hash values
 */
export const hashToSelectedValues = () => {
    const url = window.location.href;
    const fragmentIndex = url.indexOf('#');
    let selectedRows =[];
    if (fragmentIndex !== -1) {
      const fragment = url.slice(fragmentIndex + 1);
      const selectedRowsMatch = fragment.match(/sel=([^&]+)/);
      if (selectedRowsMatch) {
         selectedRows = selectedRowsMatch[1].split('|'); 
      }
    }
    return selectedRows;
}